import { Fixture } from '@cds/betting-offer';
import { DayOfRaceDate, DayOfRaceResponse, RaceEntity, RacingProviders, TimeGroupedMeetings } from '@cds/betting-offer/domain-specific/horse-racing';
import { OfferMapping } from '@cds/query-objects';
import { DetailedCreateFactory, DetailedGroupingFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { RacingConfig } from '@frontend/sports/common/client-config-data-access';
import { BaseCdsApi, BaseCdsApiFactory } from '@frontend/sports/content-distribution/feature';
import {
    NextRaceFilters,
    RacingOptionMarketTypes,
    WinGameFixture,
    WinGameFixturePreview,
    toWinGameFixture,
} from '@frontend/sports/racing/feature/models';
import { orderBy } from 'lodash-es';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import { DayOfRaceModel, toDayOfRaceModel } from '../model/day-of-race.model';

export abstract class AbstractRacingService {
    cdsApi: BaseCdsApi;

    constructor(
        cdsApiServiceFactory: BaseCdsApiFactory,
        protected racingConfig: RacingConfig,
        protected endpointUrl: string,
        protected offerGroupingService: OfferGroupingService,
        protected createFactory: DetailedCreateFactory,
        protected groupingFactory: DetailedGroupingFactory,
    ) {
        this.cdsApi = cdsApiServiceFactory({ endpoint: endpointUrl });
    }

    getDayOfRaceFixtures(dayOfRace: DayOfRaceDate): Observable<DayOfRaceModel | undefined> {
        return this.cdsApi
            .get<DayOfRaceResponse>('/day-of-race', {
                day: dayOfRace,
                raceProvider: this.provider(),
            })
            .pipe(map((value) => (value ? toDayOfRaceModel(value) : undefined)));
    }

    getMeeting(meetingId: number, day: DayOfRaceDate): Observable<WinGameFixturePreview[]> {
        return this.cdsApi.get<Fixture[]>('/meeting', { meeting: meetingId, day }).pipe(
            map((fixtures) => (fixtures ? fixtures.map(toWinGameFixture) : [])),
            catchError(() => of([])),
        );
    }

    getMeetingFixtures(meetingId: number, dayOfRace: DayOfRaceDate): Observable<WinGameFixture[]> {
        return this.cdsApi
            .get<RaceEntity[]>(`/meeting/fixtures`, {
                meeting: meetingId,
                day: dayOfRace,
                marketTypes: RacingOptionMarketTypes.WinEw,
                raceProvider: this.provider(),
                offerMapping: OfferMapping.Filtered,
            })
            .pipe(
                switchMap((fixtures) => {
                    if (!fixtures) {
                        return of([]);
                    }
                    return this.mapFixtures(fixtures);
                }),
                catchError(() => of([])),
            );
    }

    getMeetings(): Observable<TimeGroupedMeetings> {
        const empty: TimeGroupedMeetings = {
            byDay: {
                [DayOfRaceDate.Today.toLowerCase() as DayOfRaceDate.Today]: [],
                [DayOfRaceDate.Tomorrow.toLowerCase() as DayOfRaceDate.Tomorrow]: [],
            },
        };

        return this.cdsApi.get<TimeGroupedMeetings>('/meetings').pipe(
            map((model) => model ?? empty),
            catchError(() => of(empty)),
        );
    }

    getNextFixtures(nextCount: number = 0, regionIds?: string): Observable<WinGameFixture[]> {
        const fixturesCount = nextCount || this.racingConfig.nextFixturesCount;
        let resourceUrl = `/next?raceprovider=${this.provider()}&nextFixturesCount=${fixturesCount}&marketTypes=WinEW&offerMapping=Filtered`;

        if (regionIds) {
            resourceUrl += `&regionIds=${regionIds}`;
        }

        return this.cdsApi.get<RaceEntity[]>(resourceUrl).pipe(
            switchMap((value) => {
                if (value && value.length > 0) {
                    return this.buildOptionsForNextFixtures(value, regionIds ? NextRaceFilters.Ukireland : NextRaceFilters.All);
                }
                return of([] as WinGameFixture[]);
            }),
            catchError(() => of([] as WinGameFixture[])),
        );
    }

    getFixture(fixtureId: string): Observable<WinGameFixture> {
        return this.cdsApi
            .get<RaceEntity[]>('/fixtures', {
                fixtureIds: [fixtureId],
                raceProvider: this.provider(),
            })
            .pipe(
                switchMap((fix) => {
                    if (!fix || fix.length === 0) {
                        return of({} as WinGameFixture);
                    }
                    return this.mapFixture(fix[0]);
                }),
                catchError(() => of({} as WinGameFixture)),
            );
    }

    updateFixture(winGameFixture: WinGameFixture): Observable<WinGameFixture> {
        return this.offerGroupingService
            .getFixtureGrouping(winGameFixture.sport.id, '')
            .pipe(map((grouping) => this.processFixture(winGameFixture, grouping)));
    }

    protected mapFixtures(races: RaceEntity[]): Observable<WinGameFixture[]> {
        if (races.length === 0) {
            return of([]);
        }

        const sportId = races[0].fixture.sport.id;

        return this.offerGroupingService.getFixtureGrouping(sportId, 'any').pipe(
            map((grouping) =>
                races.map((race) => {
                    const winGameFixture = this.ToFixture(race) as WinGameFixture;
                    return this.processFixture(winGameFixture, grouping);
                }),
            ),
        );
    }

    private processFixture(winGameFixture: WinGameFixture, grouping: any): WinGameFixture {
        const fixture = winGameFixture as unknown as Fixture;

        if (grouping) {
            const optionGroups = this.createFactory.createOptionGroups(fixture, [], grouping, undefined, false, false, false);
            const optionSets = this.groupingFactory.getOptionSets(optionGroups, grouping, fixture.source);

            return {
                ...winGameFixture,
                optionGroups,
                optionSets,
            };
        }
        return winGameFixture;
    }

    mapFixture(race: RaceEntity): Observable<WinGameFixture> {
        return this.mapFixtures([race]).pipe(map((fixtures) => fixtures[0] || null));
    }

    getFixtures(interval?: { from?: string; to?: string }): Observable<DayOfRaceModel | undefined> {
        return this.cdsApi
            .get<DayOfRaceResponse>('/race-fixtures', {
                ...interval,
                raceProvider: this.provider(),
            })
            .pipe(map((value) => (value ? toDayOfRaceModel(value) : undefined)));
    }

    buildOptionsForNextFixtures(fixtures: RaceEntity[], filter: NextRaceFilters): Observable<WinGameFixture[]> {
        return this.mapFixtures(fixtures).pipe(
            map((winGameFixtures) =>
                winGameFixtures.map((value) => ({
                    ...value,
                    nextRaceFilter: filter,
                })),
            ),
        );
    }

    getTodaysResult(regionIds?: string): Observable<WinGameFixture[]> {
        const regionIdsQueryParam = regionIds ? `&regionIds=${regionIds}` : '';

        return this.cdsApi.post<RaceEntity[]>(`/today-result?marketTypes=WinEW&offerMapping=Filtered${regionIdsQueryParam}`).pipe(
            switchMap((fixtures) => {
                if (!fixtures || fixtures.length === 0) {
                    return of([]);
                }
                return this.mapFixtures(fixtures);
            }),
            map((fixtures) => (fixtures ? orderBy(fixtures, (f) => f.startDate, 'desc') : [])),
        );
    }

    abstract ToFixture(raceEntity: RaceEntity): WinGameFixture;

    abstract provider(): RacingProviders;
}
